import React from 'react';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="bg-white dark:bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Privacy Policy
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300">
            Last updated: October 21, 2025
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            Effective Date: October 21, 2025
          </p>
        </div>

        <div className="prose prose-lg dark:prose-invert max-w-none">
          <div className="space-y-8">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Introduction
              </h2>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                At Svarra AI ("Svarra," "we," "our," or "us"), we are committed to protecting your privacy and
                personal information. This Privacy Policy explains how we collect, use, disclose, and
                safeguard your information when you use our website (svarra.ai), AI chatbot services, and related services
                (collectively, the "Services").
              </p>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed mt-4">
                By using our Services, you agree to the collection and use of information in accordance with this policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Information We Collect
              </h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    Personal Information
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                    We collect information you provide directly to us, such as:
                  </p>
                  <ul className="mt-2 space-y-1 list-disc list-inside text-gray-600 dark:text-gray-300">
                    <li>Name and email address when you create an account</li>
                    <li>Business information including business name, type, description, and operating hours</li>
                    <li>Contact information such as phone number and business address</li>
                    <li>Payment information when you subscribe to our services (processed securely through Stripe)</li>
                    <li>Marketing email preferences (opt-in/opt-out)</li>
                    <li>Communications you send to us</li>
                    <li>Google Calendar integration data (if you connect your calendar)</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    Usage Information
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                    We automatically collect information about how you use our services, including:
                  </p>
                  <ul className="mt-2 space-y-1 list-disc list-inside text-gray-600 dark:text-gray-300">
                    <li>Log data including IP address, browser type, and pages visited</li>
                    <li>Usage patterns and interactions with our AI chatbot agents</li>
                    <li>Device information and operating system</li>
                    <li>Analytics data including conversation metrics, response times, and booking conversions</li>
                    <li>Visitor tracking through cookies for session management</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                How We Use Your Information
              </h2>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
                We use the information we collect to:
              </p>
              <ul className="space-y-2 list-disc list-inside text-gray-600 dark:text-gray-300">
                <li>Provide, maintain, and improve our AI chatbot services</li>
                <li>Process transactions and send related information (subscriptions, bookings)</li>
                <li>Send technical notices, updates, and support messages</li>
                <li>Send marketing emails (only if you opted-in during registration or in settings)</li>
                <li>Respond to your comments and questions</li>
                <li>Enable booking management and calendar integrations</li>
                <li>Generate analytics and business insights for your dashboard</li>
                <li>Develop new features and services</li>
                <li>Detect, prevent, and address technical issues and security threats</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                AI and Chat Data Handling
              </h2>
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 space-y-3">
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  <strong>Important:</strong> We use Anthropic's Claude AI to power our chatbot services. Here's how your data is handled:
                </p>
                <ul className="space-y-2 list-disc list-inside text-gray-700 dark:text-gray-300 ml-4">
                  <li>Customer conversations with your AI chatbot are temporarily stored in our database for analytics and conversation history</li>
                  <li>Conversation data is sent to Anthropic's API to generate responses but is <strong>not used to train AI models</strong></li>
                  <li>We log conversation metadata (duration, message count, booking conversions) for business analytics</li>
                  <li>Conversation logs are retained for 90 days unless you delete them sooner</li>
                  <li>All data is encrypted in transit (TLS/SSL) and at rest</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Information Sharing and Disclosure
              </h2>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
                We do not sell, trade, or rent your personal information to third parties. We may share 
                your information only in the following circumstances:
              </p>
              <ul className="space-y-2 list-disc list-inside text-gray-600 dark:text-gray-300">
                <li><strong>With your consent</strong> - We will share your information when you explicitly authorize us to do so</li>
                <li><strong>Service Providers</strong> - We share data with trusted third-party service providers:
                  <ul className="ml-6 mt-2 space-y-1">
                    <li>Anthropic (Claude AI) - AI chatbot responses</li>
                    <li>Stripe - Payment processing</li>
                    <li>SendGrid - Email delivery</li>
                    <li>Google Calendar - Calendar integrations (if you connect)</li>
                    <li>MongoDB Atlas - Secure database hosting</li>
                  </ul>
                </li>
                <li><strong>Legal Compliance</strong> - To comply with legal obligations, court orders, or government requests</li>
                <li><strong>Safety and Rights</strong> - To protect our rights, property, and safety, and that of our users</li>
                <li><strong>Business Transfers</strong> - In connection with a merger, sale, or acquisition (you will be notified)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Cookies and Tracking Technologies
              </h2>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                We use cookies and similar tracking technologies to collect information about your 
                browsing activities. You can control cookies through your browser settings, though 
                some features may not work properly if you disable cookies.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Data Security
              </h2>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                We implement appropriate technical and organizational security measures to protect your 
                personal information against unauthorized access, alteration, disclosure, or destruction. 
                However, no method of transmission over the internet is 100% secure.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Marketing Communications
              </h2>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
                We respect your communication preferences:
              </p>
              <ul className="space-y-2 list-disc list-inside text-gray-600 dark:text-gray-300">
                <li><strong>Opt-In Required</strong> - We only send marketing emails if you explicitly opt-in during registration or in your account settings</li>
                <li><strong>Unsubscribe Anytime</strong> - Every marketing email includes an unsubscribe link, or you can opt-out in your account settings</li>
                <li><strong>Transactional Emails</strong> - You will still receive important account-related emails (booking confirmations, password resets, billing) even if you unsubscribe from marketing</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Your Rights
              </h2>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
                Depending on your location, you may have the following rights:
              </p>
              <ul className="space-y-2 list-disc list-inside text-gray-600 dark:text-gray-300">
                <li><strong>Access</strong> - Request a copy of your personal information</li>
                <li><strong>Correction</strong> - Update or correct inaccurate information</li>
                <li><strong>Deletion</strong> - Request deletion of your personal information (subject to legal requirements)</li>
                <li><strong>Data Portability</strong> - Receive your data in a portable format</li>
                <li><strong>Restriction</strong> - Request restriction of processing in certain circumstances</li>
                <li><strong>Objection</strong> - Object to processing of your personal information</li>
                <li><strong>Marketing Opt-Out</strong> - Unsubscribe from marketing communications at any time</li>
              </ul>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed mt-4">
                To exercise these rights, please contact us at <a href="mailto:privacy@svarra.ai" className="text-primary-600 hover:underline">privacy@svarra.ai</a>
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Children's Privacy
              </h2>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                Our services are not intended for children under 13 years of age. We do not knowingly 
                collect personal information from children under 13. If you become aware that a child 
                has provided us with personal information, please contact us.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Changes to This Privacy Policy
              </h2>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any 
                material changes by posting the new Privacy Policy on our website and updating 
                the "Last updated" date.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Contact Us
              </h2>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                If you have any questions about this Privacy Policy, please contact us at:
              </p>
              <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-gray-700 dark:text-gray-300">
                  <strong>Email:</strong> <a href="mailto:privacy@svarra.ai" className="text-primary-600 hover:underline">privacy@svarra.ai</a><br />
                  <strong>Support:</strong> <a href="mailto:support@svarra.ai" className="text-primary-600 hover:underline">support@svarra.ai</a><br />
                  <strong>Website:</strong> <a href="https://svarra.ai" className="text-primary-600 hover:underline" target="_blank" rel="noopener noreferrer">svarra.ai</a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;